import { type RefObject, useEffect, useMemo } from 'react';

import { type Table } from '@tanstack/react-table';

import { monitorForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';

import { isSameTable } from '../../utils/table-scoping';

import { handleColumnReorder } from './use-column-reorder-monitor';
import { handleRowReorder } from './use-row-reorder-monitor';

type UseDragDropMonitorArgs<T extends object> = {
	tableRef: RefObject<HTMLTableElement>;
	charlieTableState: Table<T>;
	enableColumnReorder: boolean;
	enableRowReorder: boolean;
	onDraggingRowEnd?: (args: { table: Table<T>; draggingRow: any; destinationRow: any }) => void;
};

/**
 * Sets up drag and drop monitoring for column and row reordering.
 */
export const useDragDropMonitor = <T extends object>({
	tableRef,
	charlieTableState,
	enableColumnReorder,
	enableRowReorder,
	onDraggingRowEnd,
}: UseDragDropMonitorArgs<T>): void => {
	const columnOrderState = charlieTableState.getState?.().columnOrder;
	const hasOrder = !!columnOrderState?.length;
	const columnOrder = useMemo(() => {
		if (hasOrder) {
			return columnOrderState;
		}
		return charlieTableState.getAllLeafColumns?.().map((c) => c.id) ?? [];
	}, [columnOrderState, hasOrder, charlieTableState]);
	useEffect(() => {
		if (!enableColumnReorder && !enableRowReorder) {
			return;
		}
		return monitorForElements({
			canMonitor: ({ source }) =>
				(source.data.type === 'item-column' || source.data.type === 'item-row') &&
				isSameTable(source.element, tableRef.current),
			onDrop({ location, source }) {
				const target = location.current.dropTargets[0];
				if (!target) {
					return;
				}

				// Handle column reordering
				if (source.data.type === 'item-column' && enableColumnReorder) {
					handleColumnReorder({
						source,
						target,
						charlieTableState,
						columnOrder,
					});
					return;
				}

				// Handle row reordering
				if (source.data.type === 'item-row' && enableRowReorder) {
					handleRowReorder({
						source,
						target,
						charlieTableState,
						onDraggingRowEnd,
					});
				}
			},
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [charlieTableState, columnOrder, enableRowReorder, enableColumnReorder, onDraggingRowEnd]);
};
